import React, { useEffect, useMemo, useState } from 'react';

// Sequential teal ramp — light (top of funnel) to deep (bottom of funnel).
const TEAL_RAMP = ['#8FE7D6', '#66D4C0', '#41BAA9', '#2E9A8F', '#237E76', '#1A625D', '#123F3C'];

function getDelta(curr, prev) {
  if (prev == null) return null;
  const diff = curr - prev;
  const pct = prev === 0 ? null : (diff / prev) * 100;
  return { diff, pct };
}

export default function OpportunityFunnelView({ dataFromParent }) {
  console.log("dataFromParent ", dataFromParent);
  let data = dataFromParent ? dataFromParent : [];
  console.log("data ", data);

  const [hovered, setHovered] = useState(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 80);
    return () => clearTimeout(t);
  }, []);

  const maxCount = Math.max(...(data?.map((d) => d.lead_count) || [1]));
  const MIN_WIDTH_PCT = 16;

  const widths = useMemo(
    () =>
      data.map((d) => {
        const raw = (Math.sqrt(d.lead_count) / Math.sqrt(maxCount)) * 100;
        return Math.max(raw, MIN_WIDTH_PCT);
      }),
    [data, maxCount]
  );

  const totalLeads = data.reduce((sum, d) => sum + d.lead_count, 0);

  const svgWidth = 480;
  const cx = 165;
  const maxShapeWidth = 260;
  const rowHeight = 52;
  const gap = 7;
  const topPad = 6;
  const svgHeight = topPad * 2 + data.length * rowHeight + (data.length - 1) * gap;

  // Optimized space thresholds to allow labels to fit comfortably
  const COUNT_INSIDE_MIN_PX = 50;
  const NAME_INSIDE_MIN_PX = 100;
  const labelX = cx + maxShapeWidth / 2 + 16;

  return (
    <div style={styles.page}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');
        .lf-trapezoid-group { transform-box: fill-box; }
        .lf-list-row { transition: background 0.15s ease, border-color 0.15s ease; cursor: pointer; }
        @media (max-width: 680px) {
          .lf-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      <div style={styles.card}>
        <div style={styles.header}>
          <h1 style={styles.title}>Lead funnel</h1>
          <p style={styles.subtitle}>
            <span style={styles.mono}>{totalLeads.toLocaleString()}</span> leads across{' '}
            <span style={styles.mono}>{data.length}</span> stages
          </p>
        </div>

        <div className="lf-grid" style={styles.grid}>
          {/* Funnel visual */}
          <div style={styles.funnelWrap}>
            <svg
              viewBox={`0 0 ${svgWidth} ${svgHeight}`}
              width="100%"
              height={svgHeight}
              role="img"
              aria-label="Funnel chart showing lead counts across pipeline stages"
            >
              {data.map((d, i) => {
                const wTop = widths[i];
                const wBottom = i < data.length - 1 ? widths[i + 1] : widths[i] * 0.9;
                const topPx = (wTop / 100) * maxShapeWidth;
                const bottomPx = (wBottom / 100) * maxShapeWidth;
                const y = topPad + i * (rowHeight + gap);
                const cy = y + rowHeight / 2;
                const topX1 = cx - topPx / 2;
                const topX2 = cx + topPx / 2;
                const botX1 = cx - bottomPx / 2;
                const botX2 = cx + bottomPx / 2;
                const avgPx = (topPx + bottomPx) / 2;
                const rightEdgeX = (topX2 + botX2) / 2;
                const color = TEAL_RAMP[i % TEAL_RAMP.length];
                const isHovered = hovered === i;
                const isDimmed = hovered !== null && !isHovered;
                
                // Segment index >= 3 represents the deeper, darker background colors
                const lightText = i >= 3; 

                const countFitsInside = avgPx >= COUNT_INSIDE_MIN_PX;
                const nameFitsInside = avgPx >= NAME_INSIDE_MIN_PX;

                return (
                  <g
                    key={d.lead_CampaignId}
                    className="lf-trapezoid-group"
                    style={{
                      transform: mounted ? 'scaleX(1)' : 'scaleX(0)',
                      transformOrigin: `${cx}px ${cy}px`,
                      transition: `transform 0.6s cubic-bezier(.22,.9,.3,1) ${i * 60}ms, opacity 0.2s ease`,
                      opacity: isDimmed ? 0.4 : 1,
                    }}
                    onMouseEnter={() => setHovered(i)}
                    onMouseLeave={() => setHovered(null)}
                  >
                    <polygon
                      points={`${topX1},${y} ${topX2},${y} ${botX2},${y + rowHeight} ${botX1},${y + rowHeight}`}
                      fill={color}
                      style={{
                        filter: isHovered ? 'brightness(0.93) contrast(1.1)' : 'none',
                        transition: 'filter 0.15s ease',
                        cursor: 'pointer',
                      }}
                    />

                    {countFitsInside && (
                      <text
                        x={cx}
                        y={nameFitsInside ? cy - 5 : cy + 5}
                        textAnchor="middle"
                        style={{
                          // High-contrast solid coloring (No transparency alphas)
                          fill: lightText ? '#FFFFFF' : '#061816',
                          fontFamily: "'JetBrains Mono', monospace",
                          fontSize: 15,
                          fontWeight: 600,
                          pointerEvents: 'none',
                        }}
                      >
                        {d.lead_count.toLocaleString()}
                      </text>
                    )}

                    {nameFitsInside && (
                      <text
                        x={cx}
                        y={cy + 13}
                        textAnchor="middle"
                        style={{
                          // Fully solid colors for sub-labels to guarantee crisp readability
                          fill: lightText ? '#EAFBF7' : '#112D29',
                          fontFamily: "'Inter', sans-serif",
                          fontSize: 11,
                          fontWeight: 600,
                          letterSpacing: '0.01em',
                          pointerEvents: 'none',
                        }}
                      >
                        {d.lead_CampaignId}
                      </text>
                    )}

                    {!nameFitsInside && (
                      <g style={{ pointerEvents: 'none' }}>
                        <line
                          x1={rightEdgeX + 3}
                          y1={cy}
                          x2={labelX - 6}
                          y2={cy}
                          stroke="rgba(0,0,0,0.2)"
                          strokeWidth={1}
                        />
                        <circle cx={rightEdgeX + 3} cy={cy} r={2} fill="rgba(0,0,0,0.4)" />
                        <text
                          x={labelX}
                          y={cy + 4}
                          textAnchor="start"
                          style={{
                            fill: '#111827', 
                            fontFamily: "'Inter', sans-serif",
                            fontSize: 12,
                            fontWeight: 500,
                            letterSpacing: '0.01em',
                          }}
                        >
                          {d.lead_CampaignId}
                          {!countFitsInside && (
                            <tspan
                              style={{ fontFamily: "'JetBrains Mono', monospace", fill: '#4B5563', fontWeight: 600 }}
                            >
                              {'  '}
                              {d.lead_count.toLocaleString()}
                            </tspan>
                          )}
                        </text>
                      </g>
                    )}
                  </g>
                );
              })}
            </svg>
          </div>

          {/* Dynamic Stage Details fallback sidebar */}
          <div style={styles.list}>
            {data.map((d, i) => {
              const isHovered = hovered === i;
              return (
                <div
                  key={d.lead_CampaignId}
                  className="lf-list-row"
                  style={{
                    ...styles.listRow,
                    borderColor: isHovered ? TEAL_RAMP[i % TEAL_RAMP.length] : '#E5E7EB',
                    background: isHovered ? '#F9FAFB' : 'transparent',
                  }}
                  onMouseEnter={() => setHovered(i)}
                  onMouseLeave={() => setHovered(null)}
                >
                  <div style={styles.listRowTop}>
                    <span style={{ ...styles.swatch, background: TEAL_RAMP[i % TEAL_RAMP.length] }} />
                    <span style={styles.stageName}>{d.lead_CampaignId}</span>
                    <span style={styles.stageCount}>{d.lead_count.toLocaleString()}</span>
                  </div>
                </div>
              );
            })}
          </div>

        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    width: '100%',
    minHeight: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    justifyContent: 'center',
    fontFamily: "'Inter', sans-serif",
  },
  card: {
    width: '100%',
    maxWidth: 880,
    background: '#FFFFFF',
    border: '1px solid #E5E7EB',
    borderRadius: 20,
    padding: '32px 32px 28px',
    boxSizing: 'border-box',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
  },
  header: {
    marginBottom: 28,
  },
  title: {
    fontWeight: 600,
    fontSize: 30,
    color: '#111827',
    margin: '0 0 6px',
    letterSpacing: '-0.01em',
  },
  subtitle: {
    fontSize: 14,
    color: '#4B5563',
    margin: 0,
  },
  mono: {
    fontFamily: "'JetBrains Mono', monospace",
    color: '#1F2937',
    fontWeight: 500,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.15fr) minmax(0, 1fr)',
    gap: 32,
    alignItems: 'start',
  },
  funnelWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  listRow: {
    border: '1px solid #E5E7EB',
    borderRadius: 12,
    padding: '10px 14px',
  },
  listRowTop: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  swatch: {
    width: 9,
    height: 9,
    borderRadius: 3,
    flexShrink: 0,
  },
  stageName: {
    fontSize: 13.5,
    fontWeight: 500,
    color: '#111827',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  stageCount: {
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: 14,
    fontWeight: 500,
    color: '#111827',
  },
};